import { useState, useEffect, useMemo, useCallback, lazy, Suspense } from 'react'
import { useJourneyNav } from '@/lib/journeySections'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import { useNavigate, useSearchParams } from 'react-router-dom'
import { useTour, tourStyles } from '@/hooks/useTour'
import { Tabs, TabsContent } from '@/components/ui/tabs'
import { VideoHelpButton } from '@/components/guidance/VideoHelpButton'
import type { GuidanceSurface } from '@/lib/guidanceSurfaces'
import { getKpaDescription } from '@/utils/performance/kpaDescriptions'
import { Badge } from '@/components/ui/badge'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select'
import { Button } from '@/components/ui/button'
import IDPViewerDialog from '@/components/performance/manager/IDPViewerDialog'
import {
  Award,
  Scale,
  Grid3x3,
  BarChart3,
  ListChecks,
  Calendar,
  Sparkles,
  Search,
  Fingerprint,
  Spotlight,
  Goal
} from 'lucide-react'
import { motion } from 'framer-motion'
import { useAuth } from '@/providers/AuthProvider'
import managerService, { TeamMember, TeamPerformanceSummary } from '@/services/api/managerService'
import { useManagerTeamSummary } from '@/hooks/manager/useManagerTeamSummary'
import { idpService } from '@/services/api/idpService'
import { isReviewEligible } from '@/lib/employment'
import { serverReason, isConflict } from '@/lib/serverReason'
import { performanceService } from '@/services/api/performanceService'
import { reviewService, type ReviewSessionData } from '@/services/api/reviewService'
import { feedbackService } from '@/services/api/feedbackService'
import type { FeedbackRequest } from '@/services/api/feedbackService'
import { EnhancedEvidenceEntry } from '@/types/evidence'
import { sastToday } from '@/components/admin/lifecycle/spellCoverage'
import toast from 'react-hot-toast'

// Import extracted tab components
// Overview is the default tab — keep it eager so first paint has no Suspense flash.
// The rest are code-split via React.lazy (imported from their own modules, not the
// barrel, so each becomes its own chunk) and load only when the tab is first opened.
import { OverviewTab } from '@/components/manager-journey/tabs/OverviewTab'
const ObjectivesTab = lazy(() => import('@/components/manager-journey/tabs/ObjectivesTab').then(m => ({ default: m.ObjectivesTab })))
const ReviewsTab = lazy(() => import('@/components/manager-journey/tabs/ReviewsTab').then(m => ({ default: m.ReviewsTab })))
const CalibrationTab = lazy(() => import('@/components/manager-journey/tabs/CalibrationTab').then(m => ({ default: m.CalibrationTab })))
const TalentTab = lazy(() => import('@/components/manager-journey/tabs/TalentTab').then(m => ({ default: m.TalentTab })))
const DevelopmentTab = lazy(() => import('@/components/manager-journey/tabs/DevelopmentTab').then(m => ({ default: m.DevelopmentTab })))
const AnalyticsTab = lazy(() => import('@/components/manager-journey/tabs/AnalyticsTab').then(m => ({ default: m.AnalyticsTab })))

// Shown briefly while a lazy-loaded tab's chunk downloads on first open.
const TabLoadingFallback = () => (
  <div className="flex items-center justify-center py-16 text-gray-400">
    <div className="animate-spin rounded-full h-6 w-6 border-2 border-heritage-blue border-t-transparent" />
  </div>
)
// Lightweight hooks for tab badge counts (full data owned by fat tabs)
import { useCalibrationSessions } from '@/hooks/useCalibration'
import { useNineBoxGrid } from '@/hooks/useTalentReview'
import ConductReviewWorkspace from '@/components/performance/manager/ConductReviewWorkspace'
import ScheduleReviewDialog from '@/components/performance/manager/ScheduleReviewDialog'
import MemberDetailsDialog from '@/components/performance/manager/MemberDetailsDialog'
import ConfirmDialog from '@/components/ui/ConfirmDialog'

// Shared light-ghost-on-dark control style used by the header buttons and
// selectors on both Journey pages (Employee + Manager). Kept identical in
// both files so the two headers stay visually in sync. min-h-[44px] on mobile
// guarantees a >=44px tap target; relaxes to the compact sm size on desktop.
const HEADER_GHOST_BTN =
  'bg-white/10 border-white/30 text-white hover:bg-white/20 backdrop-blur-sm font-secondary font-medium shadow-lg min-h-[44px] sm:min-h-0 px-3'

/**
 * "Next check-in" on a member's KPA cards: today plus three months, as a
 * South African day. (toISOString() gave the UTC day, a day short between
 * 00:00 and 02:00.)
 */
export function nextCheckInDay(): string {
  const next = new Date()
  next.setMonth(next.getMonth() + 3)
  return sastToday(next)
}

export default function ManagerJourneyPage() {
  if (import.meta.env.DEV) console.log('🚀 ManagerJourneyPage LOADED - Version with feedback support')
  
  // Interactive tour management
  const { isActive: isTourActive, hasSeenTour, startTour, autoStartTour } = useTour('manager-journey')
  
  type ManagerTab = 'overview' | 'objectives' | 'reviews' | 'calibration' | 'talent' | 'development' | 'analytics'
  const MANAGER_TABS: ManagerTab[] = ['overview', 'objectives', 'reviews', 'calibration', 'talent', 'development', 'analytics']
  const [searchParams, setSearchParams] = useSearchParams()
  const urlTab = searchParams.get('tab') as ManagerTab | null
  const activeTab: ManagerTab = urlTab && MANAGER_TABS.includes(urlTab) ? urlTab : 'overview'
  // On desktop the sections are navigated from the app sidebar (nested under
  // "People Leader Hub"); the page keeps a selector for phones only.
  const setActiveTab = (value: ManagerTab) => {
    setSearchParams({ tab: value })
  }
  // In-context guidance video bound to the active tab (undefined = no surface /
  // no video → <VideoHelpButton> renders nothing). One mount governs every tab.
  const guidanceSurface: GuidanceSurface | undefined = ({
    overview: 'manager-overview',
    objectives: 'manager-objectives',
    reviews: 'checkin-dialogue',  // also shown inside ConductReviewWorkspace's own header
    development: 'manager-development',
    analytics: 'manager-analytics',
  } as Record<string, GuidanceSurface>)[activeTab]
  const [idpDialogOpen, setIdpDialogOpen] = useState(false)
  const [selectedMember, setSelectedMember] = useState<{ id: string; name: string; role?: string; fullData?: TeamMember } | null>(null)
  
  // State management for various dialogs and actions
  const [memberDetailsOpen, setMemberDetailsOpen] = useState(false)
  const [scheduleReviewOpen, setScheduleReviewOpen] = useState(false)
  const [conductReviewOpen, setConductReviewOpen] = useState(false)
  
  const [selectedReviewType, setSelectedReviewType] = useState<string>('')

  // Get authenticated manager from auth context
  const { user } = useAuth()
  
  // Year selector state
  const currentYear = new Date().getFullYear()
  const [selectedYear, setSelectedYear] = useState<number>(currentYear)
  const availableYears = Array.from({ length: 5 }, (_, i) => currentYear - 2 + i)
  
  // System launch year - years before this are "legacy" (F&O only)
  const SYSTEM_LAUNCH_YEAR = 2026
  const isLegacyYear = selectedYear < SYSTEM_LAUNCH_YEAR
  
  // Team data state
  const [teamMembers, setTeamMembers] = useState<TeamMember[]>([])
  // Review-eligible team members only (ICA/Casual workers don't take part in the
  // performance cycle). Drives objectives, calibration and talent — the
  // team-at-a-glance (Overview) and check-ins (Reviews) keep the FULL list and
  // handle contractors specially (shown separately / ad-hoc check-ins only).
  const reviewableTeamMembers = useMemo(
    () => teamMembers.filter(isReviewEligible),
    [teamMembers],
  )
  const [teamSummary, setTeamSummary] = useState<TeamPerformanceSummary | null>(null)
  // Structural one-up flag from the backend (manages a manager). Authoritative
  // gate for calibration/talent scheduling — replaces the unreliable
  // team-member 'manager' role heuristic.
  const [isOneUp, setIsOneUp] = useState(false)
  
  // Evidence state (for team member's evidence when viewing their details)
  const [memberEvidence, setMemberEvidence] = useState<EnhancedEvidenceEntry[]>([])
  
  // Feedback state (for team member's feedback requests when viewing their details)
  const [memberFeedbackRequests, setMemberFeedbackRequests] = useState<FeedbackRequest[]>([])
  const [feedbackLoading, setFeedbackLoading] = useState(false)
  
  // KPA data for conduct review workspace
  const [memberKPAData, setMemberKPAData] = useState<any[]>([])
  const [reviewSessionId, setReviewSessionId] = useState<string | undefined>()
  const [currentReviewType, setCurrentReviewType] = useState<'year-end' | 'mid-year' | 'q1-checkin' | 'q3-checkin' | 'adhoc'>('mid-year')
  const [currentReviewScheduledDate, setCurrentReviewScheduledDate] = useState<Date | null>(null)
  
  // Trigger to force MemberDetailsDialog to reload its internal data
  const [dialogRefreshTrigger, setDialogRefreshTrigger] = useState(0)
  
  // Team review sessions for member dialog
  const [teamReviewSessions, setTeamReviewSessions] = useState<ReviewSessionData[]>([])

  // Strategic Cascade Data - Built dynamically from team members' objectives
  // Transforms objectives data into the cascade format expected by StrategicCascadeViewer
  const cascadeData = useMemo(() => {
    // Collect all objectives from all team members
    const allObjectives: any[] = []
    
    teamMembers.forEach(member => {
      // Cast to any to access objective arrays
      // Backend team-summary returns pending/active/rejected arrays, enrichment adds planObjectives
      const memberData = member as any
      const memberObjectives = (memberData.planObjectives?.length > 0
        ? memberData.planObjectives
        : [
            ...(memberData.pending_objectives || []),
            ...(memberData.active_objectives || []),
          ]
      ).filter((obj: any) => obj.status !== 'rejected' && obj.status !== 'withdrawn')

      memberObjectives.forEach((obj: any) => {
        allObjectives.push({
          ...obj,
          owner: member.display_name // Use display_name from DirectReport base type
        })
      })
    })
    
    if (allObjectives.length === 0) {
      return []
    }
    
    // Transform objectives into cascade format
    // team-summary now returns full L1→L2→L3 strategic context
    return allObjectives.map(obj => ({
      pillar_name: obj.strategic_objective?.display_name || obj.strategic_objective?.name || obj.pillar?.name || 'Uncategorized',
      strategic_objective_name: obj.strategic_initiative?.name 
        ? `${obj.strategic_objective?.display_name || obj.strategic_objective?.name || ''} - ${obj.strategic_initiative.name}`
        : obj.strategic_objective?.display_name || obj.strategic_objective?.name || 'Unknown',
      kpa_name: obj.kpa?.display_name || obj.kpa?.name || 'General',
      individual_objective: {
        id: obj.id,
        title: obj.title,
        description: obj.description,
        role_type: obj.role_type,
        owner: obj.owner || 'Unassigned',
        functional_area: obj.functional_area,
        status: obj.status,
        progress: obj.progress || 0,
        start_date: obj.start_date,
        end_date: obj.end_date
      },
      primary_initiative: obj.strategic_initiative ? {
        id: obj.strategic_initiative.id,
        name: obj.strategic_initiative.name,
        description: obj.strategic_initiative.description,
        initiative_type: 'Strategic',
        status: 'active',
        owner: null,
        start_date: null,
        end_date: null
      } : undefined,
      kpis: (obj.kpis || []).map((kpi: any) => ({
        id: kpi.id,
        name: kpi.name,
        description: kpi.description || '',
        target_value: kpi.target_value || 0,
        current_value: kpi.current_value || 0,
        unit: kpi.unit || '%',
        status: kpi.status || 'on-track',
        frequency: kpi.frequency || 'quarterly',
        owner: obj.owner || 'Unassigned',
        functional_area: obj.functional_area,
        progress_percentage: kpi.target_value > 0 
          ? Math.round((kpi.current_value / kpi.target_value) * 100)
          : 0
      })),
      strategic_context: {
        // L1: Strategic Objective (Scale, Quality, Culture, Impact)
        l1: obj.strategic_objective ? {
          id: obj.strategic_objective.id,
          name: obj.strategic_objective.name,
          display_name: obj.strategic_objective.display_name,
          code: obj.strategic_objective.code
        } : null,
        l2: obj.strategic_initiative ? {
          id: obj.strategic_initiative.id,
          code: obj.strategic_initiative.code,
          name: obj.strategic_initiative.name,
          description: obj.strategic_initiative.description
        } : null,
        l3: obj.operational_initiative ? {
          id: obj.operational_initiative.id,
          code: obj.operational_initiative.code,
          name: obj.operational_initiative.name,
          description: obj.operational_initiative.description
        } : null
      }
    }))
  }, [teamMembers])

  // Load team data from API - extracted as function for re-use
  const queryClient = useQueryClient()

  // Team data via React Query. The PL Hub was firing team-summary ~4x and team-cycle
  // ~2x per load (StrictMode double-render, tab mounts, year toggles and post-mutation
  // refreshes each kicked off their own fetch). React Query DEDUPES concurrent callers
  // of the same key to a single in-flight request and serves the 5-min cache on
  // re-mount / year toggle, so the redundant calls collapse to one.
  // Shared hook (same key as the sidebar's has-direct-reports check) so the two
  // dedupe to one team-summary request instead of firing it 3x.
  const teamSummaryQuery = useManagerTeamSummary(user?.employeeId, selectedYear)
  const teamCycleQuery = useQuery({
    queryKey: ['mgr-team-cycle', user?.employeeId, selectedYear],
    queryFn: () => reviewService.getManagerTeamReviewCycle(user!.employeeId!, selectedYear),
    enabled: !!user?.employeeId,
    staleTime: 5 * 60 * 1000,
    retry: 1,
  })

  // Mirror query results into the existing state the page + child tabs read.
  // teamMembers / teamReviewSessions are written ONLY from here, so this is safe.
  const enrichedData = teamSummaryQuery.data
  useEffect(() => {
    if (!enrichedData) return
    setTeamMembers(enrichedData.team_members)
    setIsOneUp(!!enrichedData.is_one_up)
    setTeamSummary({
      managerId: enrichedData.manager_id,
      teamSize: enrichedData.summary.teamSize,
      totalObjectives: enrichedData.summary.totalObjectives,
      totalKPIs: 0, // Backend doesn't provide this yet
      averageProgress: enrichedData.summary.averageProgress,
      pendingApprovals: enrichedData.summary.pendingApprovals,
      upcomingReviews: enrichedData.summary.upcomingReviews,
      atRiskCount: enrichedData.summary.atRiskCount,
      onTrackCount: enrichedData.summary.onTrackCount,
      offTrackCount: enrichedData.summary.offTrackCount
    })
  }, [enrichedData])

  useEffect(() => {
    if (teamCycleQuery.data) setTeamReviewSessions(teamCycleQuery.data as any[])
  }, [teamCycleQuery.data])

  // onRefreshTeam: invalidate so the next read refetches once (deduped by React Query)
  // instead of each caller firing its own request.
  const loadTeamData = useCallback(async () => {
    if (!user?.employeeId) return
    await Promise.all([
      queryClient.invalidateQueries({ queryKey: ['mgr-team-summary', user.employeeId, selectedYear] }),
      queryClient.invalidateQueries({ queryKey: ['mgr-team-cycle', user.employeeId, selectedYear] }),
    ])
  }, [queryClient, user?.employeeId, selectedYear])

  // Redirect to employee journey if user has no direct reports — unless they
  // are HR, who sit in Annual I&G Review sessions from the Talent tab without
  // leading anyone (Thabo was bounced to the Team Member Journey, 8 Sep 2026).
  const navigate = useNavigate()
  const isHrRole = (user?.roles || []).some(r => r === 'admin' || r === 'hr_admin' || r === 'hrbp')
  useEffect(() => {
    if (isHrRole) return
    if (teamMembers.length === 0 && teamSummary && teamSummary.teamSize === 0) {
      toast.error('You do not have any direct reports. Redirecting to Team Member Journey...')
      navigate('/employee-journey', { replace: true })
    }
  }, [teamMembers, teamSummary, navigate, isHrRole])

  // Auto-start interactive tour on first visit (after team data loads)
  useEffect(() => {
    if (!hasSeenTour && teamMembers.length > 0) {
      return autoStartTour()
    }
  }, [hasSeenTour, teamMembers.length, autoStartTour])

  // The Talent tab is open to every People Leader on the hub, like Calibration:
  // a seated People Leader prepares their placements there, the One-Up chairs
  // from there, and the backend scopes each viewer's visible sessions. It was
  // admin / hr_admin / hrbp only, which hid the tab from the very people whose
  // preparation the review depends on.
  const canViewTalent = true

  // ============================================================================
  // LIGHTWEIGHT QUERIES FOR TAB BADGE COUNTS
  // Full calibration/talent data is owned by CalibrationTab and TalentTab
  // ============================================================================
  // Scope the badge count to sessions THIS viewer facilitates/participates in —
  // same viewer_id the CalibrationTab uses, so the badge matches the tab (and the
  // two share a React Query key, deduping to one request). Omitting viewer_id
  // returned the org-wide count (every session on the platform).
  const {
    data: calibrationData,
    isLoading: calibrationLoading,
  } = useCalibrationSessions({ year: selectedYear, viewer_id: user?.employeeId })
  const calibrationSessionCount = calibrationData?.sessions?.length || 0

  // Only fetch the 9-box when the Talent tab is actually viewable (admin/hr roles) —
  // an ordinary People Leader can't see the Talent tab, so this was a wasted request.
  const {
    data: nineBoxData,
    isLoading: nineBoxLoading
  } = useNineBoxGrid(selectedYear, undefined, { enabled: canViewTalent })

  // The 9-box grid is org-wide; scope the "high-potential" badge to THIS viewer's
  // team. Hipo boxes (matching the backend's hipo_count): performance high +
  // potential high/medium, or performance medium + potential high.
  const teamHipoCount = useMemo(() => {
    if (!nineBoxData?.positions) return 0
    const teamIds = new Set(
      teamMembers.flatMap((m: any) => [m.employee_id, m.id].filter(Boolean).map(String))
    )
    const HIPO_BOXES = ['high_high', 'high_medium', 'medium_high']
    let count = 0
    for (const box of HIPO_BOXES) {
      for (const p of ((nineBoxData.positions as any)[box] || [])) {
        if (teamIds.has(String(p.user_id))) count++
      }
    }
    return count
  }, [nineBoxData, teamMembers])

  // "my Team's Growth" badge: team members with an IDP item still awaiting the PL's
  // review (EMPLOYEE_PROPOSED). One bulk call for the whole team — no per-member fan-out.
  const teamEmployeeIds = useMemo(
    () => teamMembers.map((m: any) => m.employee_id).filter(Boolean),
    [teamMembers]
  )
  const { data: needsReviewData } = useQuery({
    queryKey: ['idp-team-needs-review', teamEmployeeIds, selectedYear],
    queryFn: () => idpService.getTeamNeedsReviewCount(teamEmployeeIds, selectedYear),
    enabled: teamEmployeeIds.length > 0,
    staleTime: 5 * 60 * 1000,
  })
  const developmentNeedsReview = needsReviewData?.count ?? 0

  const getTabStats = () => {
    // Calculate actual objective stats from team members
    let pendingCount = 0
    let atRiskCount = 0
    
    teamMembers.forEach(member => {
      const memberData = member as any
      const pendingObjectives = memberData.pending_objectives || []
      const activeObjectives = memberData.active_objectives || []
      
      pendingCount += pendingObjectives.length
      
      // Count objectives with at-risk or off-track KPIs
      activeObjectives.forEach((obj: any) => {
        const kpis = obj.kpis || []
        const hasAtRiskOrOffTrack = kpis.some((kpi: any) => 
          kpi.status === 'at-risk' || kpi.status === 'off-track'
        )
        if (hasAtRiskOrOffTrack) {
          atRiskCount++
        }
      })
    })
    
    // Analytics "insights" = team members with something worth reviewing
    // (off-track/at-risk status or an active alert). Real + team-scoped.
    const insightsCount = teamMembers.filter((m: any) =>
      m.status === 'off-track' || m.status === 'at-risk' || (m.alerts?.length || 0) > 0
    ).length

    return {
      overview: { alerts: teamSummary?.pendingApprovals || 0, reviews: teamSummary?.upcomingReviews || 0 },
      objectives: { pending: pendingCount, atRisk: atRiskCount },
      reviews: { upcoming: teamSummary?.upcomingReviews || 0, overdue: 0 },
      calibration: { scheduled: calibrationSessionCount, pending: 0 },
      talent: { highPotential: teamHipoCount, criticalRoles: 2 },
      development: { activePlans: 10, needsUpdate: developmentNeedsReview },
      analytics: { insights: insightsCount }
    }
  }

  const stats = getTabStats()

  // Each section's live count, shown by the sidebar under the section name.
  const publishJourneyNav = useJourneyNav((state) => state.publish)
  const clearJourneyNav = useJourneyNav((state) => state.clear)
  const hubSubtitles: Partial<Record<ManagerTab, string>> = {
    overview: `${stats.overview.alerts} pending`,
    objectives: `${stats.objectives.pending} pending`,
    reviews: `${stats.reviews.upcoming} upcoming`,
    calibration: calibrationLoading ? '…' : `${stats.calibration.scheduled} sessions`,
    talent: nineBoxLoading ? '…' : `${teamHipoCount} high-potential`,
    development: `${stats.development.needsUpdate} updates`,
    analytics: `${stats.analytics.insights} insights`,
  }
  const hubSubtitlesKey = JSON.stringify(hubSubtitles)
  useEffect(() => {
    publishJourneyNav('hub', { subtitles: JSON.parse(hubSubtitlesKey) })
    return () => clearJourneyNav('hub')
  }, [hubSubtitlesKey, publishJourneyNav, clearJourneyNav])

  // Load evidence for a specific team member
  const loadMemberEvidence = async (userId: string, reviewType?: string) => {
    try {
      // Convert review type to review period for backend (uppercase)
      // Q1 check-in falls in MIDYEAR period, Q3 check-in falls in YEAREND period
      let reviewPeriod: string | undefined
      if (reviewType) {
        reviewPeriod = (reviewType === 'mid-year' || reviewType === 'q1-checkin') ? 'MIDYEAR' : 'YEAREND'
      }
      
      const response = await performanceService.getUserEvidence(userId, {
        reviewPeriod
      })
      setMemberEvidence(response.evidence || [])
      if (import.meta.env.DEV) console.log(`📂 Loaded ${response.evidence?.length || 0} evidence items for review period: ${reviewPeriod || 'all'}`)
    } catch (error) {
      console.error('Failed to load member evidence:', error)
      setMemberEvidence([])
    }
  }

  // Load KPA data for a specific team member (includes measurement history)
  const loadMemberKPAData = async (userId: string) => {
    try {
      const performancePlan = await performanceService.getEmployeePerformancePlan(
        userId,
        new Date().getFullYear(),
        false // Don't include completed objectives
      )
      
      if (performancePlan.has_objectives && performancePlan.objectives.length > 0) {
        // Transform to KPAData format (grouping by KPA)
        const kpaGroups: Record<string, any> = {}
        
        // Filter out rejected AND withdrawn objectives (pending_approval should
        // be visible as read-only). Withdrawn entries are retired from the
        // active cycle and must not surface in the manager's KPA view of a
        // team member, otherwise stale objectives appear alongside live ones.
        const activeObjectives = performancePlan.objectives.filter(
          obj => obj.status !== 'rejected' && obj.status !== 'withdrawn'
        )
        
        activeObjectives.forEach(obj => {
          // Use the KPA name as the ID for consistency with AI service
          const kpaName = obj.kpa?.name || 'uncategorized'
          const kpaDisplayName = obj.kpa?.display_name || 'Uncategorized'
          
          if (!kpaGroups[kpaName]) {
            kpaGroups[kpaName] = {
              id: kpaName,  // Use name as ID for AI service consistency
              // Numeric KPA PK — evidence rows store kpa_id as this number, so
              // KPA-filtered evidence views must match on it (the slug `id`
              // above never equals the numeric kpaId the API returns).
              numericId: obj.kpa?.id,
              name: kpaName,
              display_name: kpaDisplayName,
              description: getKpaDescription(kpaDisplayName),
              strategic_pillar_id: obj.pillar?.id?.toString() || '0',
              weight_percentage: obj.kpa?.weight_percentage || 100,
              sort_order: (obj.kpa as any)?.sort_order ?? 999,
              individualObjectives: [],
              underlyingKPIs: []
            }
          }
          
          kpaGroups[kpaName].individualObjectives.push(obj)
          
          // Extract KPIs from this objective and add to underlyingKPIs (skip duplicates)
          if (obj.kpis && obj.kpis.length > 0) {
            obj.kpis.forEach(kpi => {
              // Only add if not already present (avoid duplicates from multiple objectives)
              if (!kpaGroups[kpaName].underlyingKPIs.some((k: any) => k.id === kpi.id)) {
                kpaGroups[kpaName].underlyingKPIs.push(kpi)
              }
            })
          }
        })
        
        // Calculate averageProgress and riskLevel for each KPA based on KPI achievement
        const kpaDataArray = Object.values(kpaGroups).map((kpa: any) => {
          let totalProgress = 0
          let progressCount = 0
          
          // Calculate progress from objectives and their KPIs
          kpa.individualObjectives.forEach((obj: any) => {
            if (obj.kpis && obj.kpis.length > 0) {
              // Calculate objective progress from its KPIs
              const kpiProgressSum = obj.kpis.reduce((sum: number, kpi: any) => {
                const kpiProgress = kpi.target_value > 0 
                  ? (kpi.current_value / kpi.target_value) * 100 
                  : 0
                return sum + Math.min(kpiProgress, 100)
              }, 0)
              totalProgress += kpiProgressSum / obj.kpis.length
              progressCount++
            } else if (obj.progress !== undefined) {
              // Fallback to objective.progress if no KPIs
              totalProgress += obj.progress
              progressCount++
            }
          })
          
          const averageProgress = progressCount > 0 ? Math.round(totalProgress / progressCount) : 0
          
          // Determine risk level based on KPI statuses
          const atRiskKPIs = kpa.underlyingKPIs.filter((k: any) => 
            k.status === 'at-risk' || k.status === 'off-track'
          ).length
          const riskLevel = atRiskKPIs > kpa.underlyingKPIs.length / 2 ? 'high' : 
                           atRiskKPIs > 0 ? 'medium' : 'low'
          
          return {
            ...kpa,
            averageProgress,
            riskLevel,
            totalEvidence: 0, // Will be calculated from memberEvidence
            nextReview: nextCheckInDay()
          }
        })
        
        // Sort KPAs by sort_order for consistent ordering across all views
        kpaDataArray.sort((a: any, b: any) => (a.sort_order || 999) - (b.sort_order || 999))
        
        if (import.meta.env.DEV) console.log(`🔧 Setting memberKPAData state with ${kpaDataArray.length} KPAs`)
        setMemberKPAData(kpaDataArray)
        if (import.meta.env.DEV) console.log(`✅ State set complete`)
        
        // Debug: Log measurement history counts
        kpaDataArray.forEach(kpa => {
          const historyCount = kpa.underlyingKPIs
            ?.flatMap((kpi: any) => kpi.measurement_history || [])
            .length || 0
          if (import.meta.env.DEV) console.log(`📊 KPA "${kpa.name}": ${kpa.underlyingKPIs?.length || 0} KPIs, ${historyCount} total measurements`)
        })
      } else {
        setMemberKPAData([])
      }
      // Loaded OK — objectives present, OR the member legitimately has none.
      // Distinct from the catch below (a FAILED fetch) so handleConductReview
      // doesn't silently open a check-in with no objectives/KPIs.
      return true
    } catch (error) {
      console.error('Failed to load member KPA data:', error)
      setMemberKPAData([])
      return false
    }
  }

  // Load feedback requests for a team member
  const loadMemberFeedback = async (memberId: string) => {
    if (import.meta.env.DEV) console.log(`🔍 loadMemberFeedback called for: ${memberId}`)
    setFeedbackLoading(true)
    try {
      // Get ALL feedback requests for this team member using manager endpoint
      // This includes both manager-initiated AND employee-requested feedback
      if (import.meta.env.DEV) console.log(`📡 Calling feedbackService.listTeamMemberFeedbackRequests...`)
      const requests = await feedbackService.listTeamMemberFeedbackRequests(memberId)
      if (import.meta.env.DEV) console.log(`📥 Received ${requests.length} feedback requests from API`)
      console.log(`   Raw data:`, requests)
      
      setMemberFeedbackRequests(requests)
      if (import.meta.env.DEV) console.log(`📋 State updated with ${requests.length} feedback requests`)
      console.log(`   Types: People leader-initiated=${requests.filter(r => r.request_type === 'matrix' || r.request_type === 'upward_360').length}, Team member-requested=${requests.filter(r => r.request_type === 'employee_requested').length}`)
    } catch (error) {
      console.error('❌ Failed to load member feedback:', error)
      setMemberFeedbackRequests([])
      toast.error('Failed to load feedback requests')
    } finally {
      setFeedbackLoading(false)
    }
  }

  // Handler functions following KPAWorkspace pattern
  const handleViewMember = async (memberId: string) => {
    if (import.meta.env.DEV) console.log(`🚪 handleViewMember called for:`, memberId)
    const member = teamMembers.find(m => m.employee_id === memberId)
    if (member) {
      setSelectedMember({ id: member.employee_id, name: member.display_name ?? 'Unknown', role: member.job_title ?? 'No Title', fullData: member })
      
      // Load member's evidence, KPA data (with measurement history), and feedback BEFORE opening dialog
      if (import.meta.env.DEV) console.log(`📥 Loading evidence, KPA data, and feedback for ${memberId}...`)
      
      // Load data sequentially with individual error handling for debugging
      try {
        if (import.meta.env.DEV) console.log('  → Loading evidence...')
        await loadMemberEvidence(member.employee_id)
        if (import.meta.env.DEV) console.log('  ✓ Evidence loaded')
      } catch (err) {
        console.error('  ✗ Evidence failed:', err)
      }
      
      try {
        if (import.meta.env.DEV) console.log('  → Loading KPA data...')
        await loadMemberKPAData(member.employee_id)
        if (import.meta.env.DEV) console.log('  ✓ KPA data loaded')
      } catch (err) {
        console.error('  ✗ KPA data failed:', err)
      }
      
      try {
        if (import.meta.env.DEV) console.log('  → Loading feedback...')
        await loadMemberFeedback(member.employee_id)
        if (import.meta.env.DEV) console.log('  ✓ Feedback loaded')
      } catch (err) {
        console.error('  ✗ Feedback failed:', err)
      }
      
      if (import.meta.env.DEV) console.log(`✅ All data loaded, current memberKPAData length:`, memberKPAData.length)
      
      // Open dialog AFTER data is loaded
      setMemberDetailsOpen(true)
      if (import.meta.env.DEV) console.log(`🚪 Dialog opened`)
    }
  }

  // Evidence handlers
  const handleAddEvidence = async (evidence: Omit<EnhancedEvidenceEntry, 'id' | 'createdBy' | 'updatedAt'>): Promise<EnhancedEvidenceEntry | undefined> => {
    if (!selectedMember?.id) throw new Error('No team member is selected.')

    try {
      // Use provided review period if available, otherwise fall back to currentReviewType
      const evidenceWithPeriod = {
        ...evidence,
        reviewPeriod: evidence.reviewPeriod || (currentReviewType === 'mid-year' ? 'MIDYEAR' : 'YEAREND')
      }
      
      if (import.meta.env.DEV) console.log('📤 ManagerJourneyPage - Creating Evidence:', {
        providedPeriod: evidence.reviewPeriod,
        currentReviewType,
        finalPeriod: evidenceWithPeriod.reviewPeriod
      })
      
      const response = await performanceService.createEvidence(selectedMember.id, evidenceWithPeriod)
      
      // If evidence has KPI measurement, record it
      if (evidence.kpiId && evidence.measurementValue !== undefined) {
        try {
          await performanceService.recordEmployeeKPIMeasurement(selectedMember.id, evidence.kpiId, {
            value: evidence.measurementValue,
            notes: evidence.description || evidence.title,
            evidence_id: response.evidence?.id,
            period: evidenceWithPeriod.reviewPeriod  // Pass the review period
          })
        } catch (err) {
          console.error('Failed to record KPI measurement:', err)
        }
      }
      
      // Reload KPA data FIRST (has measurement history), then evidence
      if (import.meta.env.DEV) console.log('🔄 Refreshing KPA data with measurement history...')
      await loadMemberKPAData(selectedMember.id)
      if (import.meta.env.DEV) console.log('🔄 Refreshing evidence list...')
      await loadMemberEvidence(selectedMember.id)
      if (import.meta.env.DEV) console.log('✅ Refresh complete')
      
      return response.evidence // Return created evidence for attachment upload
    } catch (error) {
      console.error('Failed to add evidence:', error)
      // Let the form say it didn't save and keep what was typed. Returning
      // nothing here closed it as if the entry had saved (HR UAT list #6).
      throw error
    }
  }

  const handleClassifyEvidence = async (evidenceId: string, classification: EnhancedEvidenceEntry['classification']) => {
    if (!selectedMember?.id) return
    
    try {
      await performanceService.updateEvidenceClassification(selectedMember.id, evidenceId, classification)
      
      // Update local state
      setMemberEvidence(prev => prev.map(ev => 
        ev.id === evidenceId ? { ...ev, classification } : ev
      ))
    } catch (error) {
      console.error('Failed to classify evidence:', error)
    }
  }

  const handleUpdateEvidence = async (evidenceId: string, updates: Partial<EnhancedEvidenceEntry>) => {
    if (import.meta.env.DEV) console.log('ManagerJourneyPage.handleUpdateEvidence called', { 
      evidenceId, 
      updates, 
      selectedMemberId: selectedMember?.id 
    })
    
    if (!selectedMember?.id) {
      console.warn('No selected member ID')
      return
    }
    
    try {
      if (import.meta.env.DEV) console.log('Calling performanceService.updateEvidence...')
      const response = await performanceService.updateEvidence(selectedMember.id, evidenceId, updates)
      if (import.meta.env.DEV) console.log('API call successful', response)
      console.log('Response structure:', { 
        hasEvidence: !!response?.evidence, 
        evidenceId: response?.evidence?.id,
        visibility: response?.evidence?.visibility 
      })
      
      // If measurement was updated, record the KPI measurement to update midyear_value/yearend_value
      if (updates.measurementValue !== undefined && updates.kpiId) {
        try {
          // Determine review period from updates or response
          const reviewPeriod = updates.reviewPeriod || response?.evidence?.reviewPeriod || 
            (currentReviewType === 'mid-year' ? 'MIDYEAR' : 'YEAREND')
          
          if (import.meta.env.DEV) console.log('📊 Recording KPI measurement:', {
            kpiId: updates.kpiId,
            value: updates.measurementValue,
            period: reviewPeriod
          })
          
          await performanceService.recordEmployeeKPIMeasurement(selectedMember.id, updates.kpiId, {
            value: updates.measurementValue,
            notes: updates.description || updates.title || 'People Leader measurement update',
            evidence_id: evidenceId,
            period: reviewPeriod
          })
        } catch (err) {
          console.error('Failed to record KPI measurement:', err)
        }
      }
      
      // Reload KPA data FIRST (has measurement history), then evidence
      if (import.meta.env.DEV) console.log('🔄 Refreshing KPA data with measurement history...')
      await loadMemberKPAData(selectedMember.id)
      if (import.meta.env.DEV) console.log('🔄 Refreshing evidence list...')
      await loadMemberEvidence(selectedMember.id)
      if (import.meta.env.DEV) console.log('✅ Refresh complete')
      
      toast.success('Entry updated successfully')
    } catch (error) {
      console.error('Failed to update evidence:', error)
      toast.error('Failed to update entry')
    }
  }

  const handleDeleteEvidence = async (evidenceId: string) => {
    if (!selectedMember?.id) return

    try {
      await performanceService.deleteEvidence(selectedMember.id, evidenceId)

      // Remove from local state
      setMemberEvidence(prev => prev.filter(ev => ev.id !== evidenceId))

      // Refresh KPA data so KPI progress reflects the rolled-back measurement.
      // Backend rolls back EmployeeKPIProgress when measurement-bearing evidence
      // is deleted; without this refetch the UI shows stale progress until reload.
      await loadMemberKPAData(selectedMember.id)
    } catch (error) {
      console.error('Failed to delete evidence:', error)
    }
  }

  const handleUploadDocument = async (file: File, evidenceData: Partial<EnhancedEvidenceEntry>) => {
    if (!selectedMember?.id) return
    
    try {
      // 1. Create evidence entry first
      const response = await performanceService.createEvidence(selectedMember.id, {
        ...evidenceData,
        type: 'document' as const,
        reviewPeriod: evidenceData.reviewPeriod || (currentReviewType === 'mid-year' ? 'MIDYEAR' : 'YEAREND')
      } as any)
      
      // 2. Upload the file as an attachment to the created evidence
      // Backend returns { evidence: { id: "..." } }, so access nested evidence object
      const evidenceId = response?.evidence?.id || response?.id
      if (evidenceId) {
        await performanceService.uploadAttachment(evidenceId, file)
      }
      
      // 3. Reload all evidence and KPA data
      await loadMemberEvidence(selectedMember.id)
      await loadMemberKPAData(selectedMember.id)
    } catch (error) {
      console.error('Failed to upload document:', error)
    }
  }

  // Objective handlers (NEW - for KPA panels)
  const handleCreateObjective = (objective: any) => {
    if (import.meta.env.DEV) console.log('Create objective:', objective)
    // TODO: POST /performance/objectives
  }

  const handleUpdateObjective = (objectiveId: number, updates: any) => {
    if (import.meta.env.DEV) console.log('Update objective:', objectiveId, updates)
    // TODO: PATCH /performance/objectives/{objectiveId}
  }

  const handleDeleteObjective = (objectiveId: number) => {
    if (import.meta.env.DEV) console.log('Delete objective:', objectiveId)
    // TODO: DELETE /performance/objectives/{objectiveId}
  }

  const handleViewObjectiveDetails = (objective: any) => {
    if (import.meta.env.DEV) console.log('View objective details:', objective)
    // TODO: Open objective details modal or navigate to objective workspace
  }

  const handleApproveObjective = async (objectiveId: number, feedback?: string, edits?: any) => {
    try {
      await performanceService.approveObjective(objectiveId, {
        feedback,
        edits: edits || undefined
      })
      toast.success('Objective approved successfully')
      // Refresh team data
      await loadTeamData()
    } catch (error) {
      console.error('Failed to approve objective:', error)
      toast.error('Failed to approve objective')
    }
  }

  const handleRejectObjective = async (objectiveId: number, reason: string) => {
    try {
      await performanceService.rejectObjective(objectiveId, { reason })
      toast.success('Objective rejected successfully')
      // Refresh team data
      await loadTeamData()
    } catch (error) {
      console.error('Failed to reject objective:', error)
      toast.error('Failed to reject objective')
    }
  }

  const handleScheduleReview = (memberId: string, reviewType?: string) => {
    const member = teamMembers.find(m => m.employee_id === memberId)
    if (member) {
      setSelectedMember({ id: member.employee_id, name: member.display_name ?? 'Unknown', role: member.job_title ?? 'No Title', fullData: member })
      setSelectedReviewType(reviewType || '')
      setScheduleReviewOpen(true)
    }
  }

  const handleConductReview = async (memberId: string, sessionId: string) => {
    const member = teamMembers.find(m => m.employee_id === memberId)
    if (member) {
      const memberData = { id: member.employee_id, name: member.display_name ?? 'Unknown', role: member.job_title ?? 'No Title', fullData: member }
      setSelectedMember(memberData)
      
      // Load ALL data BEFORE opening the dialog to avoid "No Objectives" flash
      try {
        // 1. FIRST: Load the session to get the review type
        let reviewType: 'year-end' | 'mid-year' | 'q1-checkin' | 'q3-checkin' | 'adhoc' | undefined
        try {
          const session = await reviewService.getReviewSession(sessionId)
          // Map backend review type to frontend format
          const reviewTypeMap: Record<string, 'year-end' | 'mid-year' | 'q1-checkin' | 'q3-checkin' | 'adhoc'> = {
            'yearend': 'year-end',
            'midyear': 'mid-year',
            'q1_checkin': 'q1-checkin',
            'q3_checkin': 'q3-checkin',
            'adhoc': 'adhoc',
            'jit': 'adhoc'
          }
          reviewType = reviewTypeMap[session.review_type.toLowerCase()]
          if (reviewType) {
            setCurrentReviewType(reviewType)
            if (import.meta.env.DEV) console.log('📋 Loaded review type:', reviewType, 'from session:', sessionId)
          }
          // Store scheduled date for period determination (adhoc check-ins use this)
          const scheduledDate = session.review_date || (session as any).scheduled_date
          if (scheduledDate) {
            setCurrentReviewScheduledDate(new Date(scheduledDate))
          }
        } catch (error) {
          console.error('Failed to load review session type:', error)
        }
        
        // 2. Load member's evidence. Load ALL periods (not period-filtered):
        //    the review workspace and member-details filter by period
        //    client-side, and the Impact Portfolio tab filters by RELEASE
        //    (session / formal period) — a backend capture-period filter would
        //    wrongly drop an entry released for this session but captured in the
        //    other period (e.g. feedback captured YEAREND, released for Mid-Year).
        await loadMemberEvidence(memberId)
        
        // 3. Load KPA data (includes measurement history). If this FAILS (token
        //    expiry / network blip), do NOT open the check-in with empty
        //    objectives/KPIs — that produced check-ins rated with nothing visible
        //    and empty snapshots. Surface the error and bail so the manager retries.
        const kpaLoaded = await loadMemberKPAData(memberId)
        if (!kpaLoaded) {
          toast.error("Couldn't load this team member's objectives — please try again.")
          setReviewSessionId(undefined)
          return
        }

        // 4. Set the review session ID
        setReviewSessionId(sessionId)
        
        // 5. Open dialog AFTER data is loaded
        setConductReviewOpen(true)
      } catch (error) {
        console.error('Failed to load review data:', error)
        toast.error('Failed to load check-in data')
        setMemberKPAData([])
        setReviewSessionId(undefined)
      }
    }
  }

  const handleCloseMemberDetails = () => {
    setMemberDetailsOpen(false)
    setSelectedMember(null)
    setMemberFeedbackRequests([])  // Clear feedback data
  }

  // Feedback handlers
  const handleCreateFeedbackRequest = async (requestData: any) => {
    if (!selectedMember?.id || !user?.employeeId) return
    
    try {
      const payload = {
        ...requestData,
        requested_for_user_id: selectedMember.id,
        requested_by_user_id: user.employeeId
      }
      
      if (import.meta.env.DEV) console.log('📤 Creating feedback request with payload:', payload)
      
      const newRequest = await feedbackService.createFeedbackRequest(payload)
      
      toast.success('Feedback request created successfully')
      
      // Reload feedback requests
      await loadMemberFeedback(selectedMember.id)
      
      return newRequest
    } catch (error: any) {
      console.error('Failed to create feedback request:', error)
      console.error('Error response:', error.response?.data)
      
      // Handle validation errors (422)
      if (error.response?.status === 422) {
        const validationErrors = error.response?.data?.detail
        if (Array.isArray(validationErrors)) {
          const errorMessages = validationErrors.map((err: any) => 
            `${err.loc?.join('.') || 'Field'}: ${err.msg}`
          ).join(', ')
          toast.error(`Validation Error: ${errorMessages}`)
        } else if (typeof validationErrors === 'string') {
          toast.error(`Validation Error: ${validationErrors}`)
        } else {
          toast.error('Invalid request data')
        }
      } else {
        const errorMessage = typeof error.response?.data?.detail === 'string' 
          ? error.response.data.detail 
          : 'Failed to create feedback request'
        toast.error(errorMessage)
      }
      throw error
    }
  }

  const handleRefreshFeedback = async () => {
    if (!selectedMember?.id) return
    await loadMemberFeedback(selectedMember.id)
  }

  const handleCloseScheduleReview = () => {
    setScheduleReviewOpen(false)
    setSelectedMember(null)
    setSelectedReviewType('')
  }

  const handleCloseConductReview = async () => {
    setConductReviewOpen(false)
    setSelectedMember(null)
    setMemberEvidence([])
    setMemberKPAData([])
    setReviewSessionId(undefined)
    setCurrentReviewScheduledDate(null)
    
    // Refresh team data to show updated review statuses
    if (user?.employeeId) {
      await loadTeamData()
    }
  }

  const handleCloseIDP = () => {
    setIdpDialogOpen(false)
    setSelectedMember(null)
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 via-blue-50/30 to-purple-50/20 px-3 sm:px-4 md:px-6 lg:px-8 pb-0">
      {/* Tour Styles Injection */}
      <style>{tourStyles}</style>
      
      <div className="w-full mx-auto py-6 sm:py-8 max-w-[98vw] 2xl:max-w-[1800px] min-w-0 overflow-x-clip">
        {/* Header */}
        <motion.header 
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          className="mb-6 bg-gradient-to-br from-heritage-blue via-heritage-blue/95 to-future-blue text-white relative overflow-hidden rounded-2xl shadow-2xl border border-white/20"
          data-tour="manager-header"
        >
          <div className="absolute inset-0">
            <div className="absolute inset-0 bg-gradient-to-br from-white/10 via-transparent to-black/5 pointer-events-none" />
            <div className="absolute top-0 right-0 w-96 h-96 bg-gradient-to-br from-future-blue/20 to-transparent rounded-full -translate-y-48 translate-x-48" />
          </div>
          
          <div className="relative px-3 sm:px-4 md:px-6 lg:px-8 pt-3 sm:pt-6 pb-2 sm:pb-4">
            <div className="flex flex-wrap items-center justify-between gap-2 sm:gap-4 md:gap-6 min-w-0 mb-3">
              <div className="flex items-center gap-2 sm:gap-4 md:gap-6 min-w-0">
                <motion.div
                  initial={{ scale: 0.9, opacity: 0 }}
                  animate={{ scale: 1, opacity: 1 }}
                  transition={{ duration: 0.5, delay: 0.2 }}
                  className="relative hidden xl:block"
                >
                  <div className="absolute inset-0 bg-white/20 rounded-2xl blur-xl" />
                  <img src="/assets/logos/Light-Logo-Impact-Growth2.png" alt="My Impact & Growth Journey" className="h-16 object-contain drop-shadow-[0_2px_8px_rgba(0,0,0,0.3)]" />
                </motion.div>
                <div className="h-16 w-px bg-gradient-to-br from-transparent via-white/30 to-transparent mx-2 hidden xl:block" />
                <motion.div
                  initial={{ x: -30, opacity: 0 }}
                  animate={{ x: 0, opacity: 1 }}
                  transition={{ duration: 0.6, delay: 0.3 }}
                >
                  <div className="flex items-center gap-2 sm:gap-3">
                    <h1 className="font-primary text-lg sm:text-xl md:text-2xl lg:text-3xl xl:text-4xl font-bold text-white tracking-tight">
                      People Leader Hub
                    </h1>
                  </div>
                </motion.div>
              </div>

              <motion.div
                initial={{ x: 30, opacity: 0 }}
                animate={{ x: 0, opacity: 1 }}
                transition={{ duration: 0.6, delay: 0.4 }}
                className="flex flex-wrap items-center justify-end gap-2 min-w-0"
              >
                {/* Take Tour Button */}
                <Button
                  variant="outline"
                  size="sm"
                  onClick={startTour}
                  className={HEADER_GHOST_BTN}
                  title="Explore the platform"
                >
                  <Sparkles className="h-3.5 w-3.5" />
                  <span className="ml-1.5 hidden lg:inline text-xs">Explore</span>
                </Button>

                {(teamSummary?.pendingApprovals || 0) > 0 && (
                  <Badge className="bg-white/15 text-white border border-white/30 backdrop-blur-sm shadow-lg px-4 py-2 text-sm">
                    <ListChecks className="h-4 w-4 mr-2" />
                    {teamSummary?.pendingApprovals} pending {teamSummary?.pendingApprovals === 1 ? 'action' : 'actions'}
                  </Badge>
                )}
              </motion.div>
            </div>

            {/* Year selector (left) + in-context guidance Watch button (bottom-right). */}
            <div className="flex items-end justify-between gap-3">
            <motion.div
              initial={{ y: 10, opacity: 0 }}
              animate={{ y: 0, opacity: 1 }}
              transition={{ duration: 0.5, delay: 0.4 }}
              className="flex flex-wrap items-center gap-2 mb-2"
            >
              {/* Global Year Selector - Below Title */}
              <Select
                value={selectedYear.toString()}
                onValueChange={(value) => setSelectedYear(parseInt(value))}
              >
                <SelectTrigger className="w-[105px] sm:w-[115px] bg-white/10 border-white/30 text-white hover:bg-white/20 backdrop-blur-sm h-9 sm:h-10 text-xs sm:text-sm">
                  <Calendar className="h-4 w-4 mr-1 sm:mr-2 shrink-0" />
                  <span>{selectedYear}</span>
                </SelectTrigger>
                <SelectContent>
                  {availableYears.map(year => (
                    <SelectItem key={year} value={year.toString()}>
                      <div className="flex items-center gap-2">
                        {year}
                        {year < SYSTEM_LAUNCH_YEAR && <span className="text-xs bg-amber-100 text-amber-700 px-1.5 py-0.5 rounded">Legacy</span>}
                        {year === currentYear && <span className="text-xs bg-green-100 text-green-700 px-1.5 py-0.5 rounded">Current</span>}
                      </div>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </motion.div>
            {guidanceSurface && (
              <VideoHelpButton
                surface={guidanceSurface}
                className="mb-2 shrink-0 border-white/30 bg-white/15 text-white hover:bg-white/25"
              />
            )}
            </div>
          </div>
        </motion.header>

        {/* Main Content - Tabs */}
        <motion.section 
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.2 }}
          className="mb-8"
        >
          <Tabs value={activeTab} onValueChange={(value) => setActiveTab(value as any)} className="w-full">
            <div className="sticky top-0 z-20 m-0 bg-white/90 backdrop-blur-sm rounded-t-2xl border border-white/50 shadow-xl min-w-0 max-w-full md:hidden">
              {/* Mobile: Dropdown tab selector */}
              <div className="md:hidden p-3">
                <Select value={activeTab} onValueChange={(value) => setActiveTab(value as any)}>
                  <SelectTrigger className="w-full h-11 bg-white border-gray-200 text-sm font-semibold">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="overview">Team "at-a-glance"</SelectItem>
                    <SelectItem value="objectives">Configuring Objectives</SelectItem>
                    <SelectItem value="reviews">1:1 Check-In Dialogues</SelectItem>
                    <SelectItem value="calibration">Calibration</SelectItem>
                    {canViewTalent && <SelectItem value="talent">Talent</SelectItem>}
                    <SelectItem value="development">my Team's Growth Plans</SelectItem>
                    <SelectItem value="analytics">Team Analytics</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>

            {/* Tab Content */}
            <div>
            <div className="bg-white/90 backdrop-blur-sm border border-white/50 shadow-xl px-3 sm:px-6 md:px-0 min-w-0 overflow-x-clip flex-1 rounded-b-2xl border-t-0 md:rounded-2xl md:border-t">
              {/* Legacy Year View - Show F&O historical ratings */}
              {isLegacyYear && (
                <div className="p-8">
                  <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    className="max-w-4xl mx-auto text-center py-12"
                  >
                    <div className="bg-gradient-to-br from-amber-50 to-orange-50 rounded-2xl p-12 border-2 border-amber-200">
                      <div className="flex justify-center mb-6">
                        <div className="p-4 bg-amber-100 rounded-full">
                          <Award className="h-12 w-12 text-amber-600" />
                        </div>
                      </div>
                      <h3 className="text-2xl font-bold text-gray-900 mb-4">
                        Viewing Historical Team Impact & Growth Data 📊
                      </h3>
                      <p className="text-lg text-gray-700 mb-6">
                        Team impact & growth data for {selectedYear} is from the legacy Finance & Operations (F&O) system.
                      </p>
                      <div className="bg-white rounded-xl p-6 mb-6 text-left">
                        <h4 className="font-semibold text-gray-900 mb-3">What you can view:</h4>
                        <ul className="space-y-3 text-gray-700">
                          <li className="flex items-start gap-3">
                            <div className="p-1 bg-amber-100 rounded-full mt-1">
                              <div className="w-2 h-2 bg-amber-600 rounded-full" />
                            </div>
                            <span><strong>Team impact & growth indicators</strong> from completed review cycles</span>
                          </li>
                          <li className="flex items-start gap-3">
                            <div className="p-1 bg-amber-100 rounded-full mt-1">
                              <div className="w-2 h-2 bg-amber-600 rounded-full" />
                            </div>
                            <span><strong>Historical summaries</strong> and calibration data</span>
                          </li>
                          <li className="flex items-start gap-3">
                            <div className="p-1 bg-amber-100 rounded-full mt-1">
                              <div className="w-2 h-2 bg-amber-600 rounded-full" />
                            </div>
                            <span><strong>Archived team reports</strong> from F&O</span>
                          </li>
                        </ul>
                      </div>
                      <div className="flex items-center justify-center gap-4">
                        <div className="text-sm text-gray-600 bg-white px-4 py-2 rounded-lg">
                          <strong>Note:</strong> This data is read-only and cannot be edited
                        </div>
                      </div>
                      <div className="mt-6">
                        <Button
                          size="lg"
                          onClick={() => setSelectedYear(currentYear)}
                          className="bg-gradient-to-r from-heritage-blue to-future-blue text-white"
                        >
                          <Calendar className="h-5 w-5 mr-2" />
                          Return to Current Year ({currentYear})
                        </Button>
                      </div>
                    </div>
                  </motion.div>
                </div>
              )}

              {/* Regular Tab Content - Hidden for Legacy Years */}
              {!isLegacyYear && (
                <Suspense fallback={<TabLoadingFallback />}>
            <TabsContent value="overview" className="mt-0 p-3 md:p-6">
              <OverviewTab
                teamMembers={teamMembers}
                isLegacyYear={isLegacyYear}
                selectedYear={selectedYear}
                onScheduleReview={(memberId) => handleScheduleReview(memberId)}
                onViewMember={handleViewMember}
              />
            </TabsContent>

            <TabsContent value="objectives" className="mt-0 p-3 md:p-6">
              <ObjectivesTab
                managerId={user?.employeeId || ''}
                teamMembers={reviewableTeamMembers}
                selectedYear={selectedYear}
                isLegacyYear={isLegacyYear}
                cascadeData={cascadeData}
                onConfigureObjectives={handleViewMember}
                onRefreshTeam={loadTeamData}
                onBulkAssignment={() => {
                  // TODO: Open bulk assignment dialog
                  if (import.meta.env.DEV) console.log('Bulk assignment - TODO: Implement dialog')
                }}
                onNavigateToMainTab={(tab) => setActiveTab(tab as any)}
              />
            </TabsContent>

            <TabsContent value="reviews" className="mt-0 p-3 md:p-6">
              <ReviewsTab
                managerId={user?.employeeId || ''}
                teamMembers={teamMembers}
                currentYear={selectedYear}
                isLegacyYear={isLegacyYear}
                selectedYear={selectedYear}
                refreshTrigger={dialogRefreshTrigger}
                teamReviewSessions={teamReviewSessions}
                onScheduleReview={handleScheduleReview}
                onConductReview={handleConductReview}
                onRefreshReviews={loadTeamData}
              />
            </TabsContent>

            {/* Calibration Workspace - available to every People Leader on the hub */}
            <TabsContent value="calibration" className="mt-0 p-3 md:p-6">
              <CalibrationTab
                user={{
                  employeeId: user?.employeeId,
                  displayName: user?.displayName,
                  email: user?.email,
                  roles: user?.roles,
                  department: user?.department,
                  managerEmployeeId: (user as any)?.managerEmployeeId,
                  managerId: (user as any)?.managerId,
                }}
                selectedYear={selectedYear}
                isLegacyYear={isLegacyYear}
                teamMembers={reviewableTeamMembers}
                teamReviewSessions={teamReviewSessions}
                isOneUp={isOneUp}
              />
            </TabsContent>

            {/* Talent Review Dashboard - Only visible to admin, hr_admin, hrbp */}
            {canViewTalent && (
            <TabsContent value="talent" className="mt-0 p-3 md:p-6">
              <TalentTab
                isOneUp={isOneUp}
                user={{
                  id: user?.id ? parseInt(String(user.id)) : undefined,
                  employeeId: user?.employeeId,
                  displayName: user?.displayName,
                  email: user?.email,
                  roles: user?.roles,
                  department: user?.department,
                }}
                selectedYear={selectedYear}
                isLegacyYear={isLegacyYear}
                teamMembers={reviewableTeamMembers}
              />
            </TabsContent>
            )}

            <TabsContent value="development" className="mt-0 p-3 md:p-6">
              <DevelopmentTab
                teamMembers={teamMembers}
                isLegacyYear={isLegacyYear}
                selectedYear={selectedYear}
                onViewIDP={(memberId: string) => {
                  const member = teamMembers.find(m => m.employee_id === memberId)
                  if (member) {
                    setSelectedMember({ id: member.employee_id, name: member.display_name ?? 'Unknown', role: member.job_title ?? 'No Title', fullData: member })
                    setIdpDialogOpen(true)
                  }
                }}
              />
            </TabsContent>

            <TabsContent value="analytics" className="mt-0 p-3 md:p-6">
              <AnalyticsTab
                teamMembers={teamMembers}
                teamSummary={teamSummary}
                teamReviewSessions={teamReviewSessions}
                isLegacyYear={isLegacyYear}
                selectedYear={selectedYear}
                managerId={user?.employeeId || ''}
              />
            </TabsContent>
                </Suspense>
              )}
            </div>
            </div>
          </Tabs>
        </motion.section>
      </div>

      {/* IDP Viewer Dialog */}
      {selectedMember && idpDialogOpen && (
        <IDPViewerDialog
          open={idpDialogOpen}
          onOpenChange={handleCloseIDP}
          memberName={selectedMember.name}
          memberId={selectedMember.id}
          memberRole={selectedMember.role}
        />
      )}

      {/* Member Details Dialog - Enhanced with KPA Support */}
      {selectedMember && memberDetailsOpen && (() => {
        if (import.meta.env.DEV) console.log(`🎯 ManagerJourneyPage rendering MemberDetailsDialog with memberKPAData:`, memberKPAData.length, 'items')
        return (
          <MemberDetailsDialog
            open={memberDetailsOpen}
            onClose={handleCloseMemberDetails}
            memberName={selectedMember.name}
            memberId={selectedMember.id}
            currentUserId={user?.employeeId}
            
            // Full enriched team member data from API
            memberData={selectedMember.fullData}
            
            // KPA Data - Loaded from API with measurement history
            memberKPAs={memberKPAData}
            
            // Refresh trigger - increments when data should be reloaded
            refreshTrigger={dialogRefreshTrigger}
          
          // Evidence Data - Loaded from API
          evidence={memberEvidence}
          
          // Evidence Handlers
          onAddEvidence={handleAddEvidence}
          onUpdateEvidence={handleUpdateEvidence}
          onUpdateEvidenceClassification={handleClassifyEvidence}
          onDeleteEvidence={handleDeleteEvidence}
          onUploadDocument={handleUploadDocument}
          onRefreshEvidence={async () => {
            if (selectedMember?.id) {
              await loadMemberEvidence(selectedMember.id)
              await loadMemberKPAData(selectedMember.id)
              // Trigger dialog to reload its internal data
              setDialogRefreshTrigger(prev => prev + 1)
            }
          }}
          
          // Objective Handlers (NEW)
          onCreateObjective={handleCreateObjective}
          onUpdateObjective={handleUpdateObjective}
          onDeleteObjective={handleDeleteObjective}
          onViewObjectiveDetails={handleViewObjectiveDetails}
          onApproveObjective={handleApproveObjective}
          onRejectObjective={handleRejectObjective}
          
          // Legacy Handlers (deprecated - use TeamReviewManagement instead)
          onConductReview={() => {
            handleCloseMemberDetails()
            // Note: This legacy handler doesn't have sessionId - should use TeamReviewManagement
            console.warn('Legacy onConductReview called without sessionId')
          }}
          onScheduleReview={() => {
            handleCloseMemberDetails()
            handleScheduleReview(selectedMember.id)
          }}
          onViewIDP={() => {
            handleCloseMemberDetails()
            setIdpDialogOpen(true)
          }}
          
          // Strategic Cascade Data - Filter by member
          memberCascadeData={cascadeData.filter(item => 
            item.individual_objective?.owner === selectedMember.name
          )}
          
          // Feedback Data - Loaded from API
          feedbackRequests={memberFeedbackRequests}
          onCreateFeedbackRequest={handleCreateFeedbackRequest}
          onRefreshFeedback={handleRefreshFeedback}
          
          // Member role for API data loading
          memberRole={selectedMember.role?.toLowerCase().replace(/\s+/g, '-') || 'lecturer'}
          
          // Review sessions for this member - for accurate next review and completion status
          memberReviewSessions={teamReviewSessions.filter(s => s.user_id === selectedMember.id)}
        />
        )
      })()}

      {/* Schedule 1:1 check-in dialogue Dialog */}
      {selectedMember && scheduleReviewOpen && (
        <ScheduleReviewDialog
          open={scheduleReviewOpen}
          onClose={handleCloseScheduleReview}
          memberName={selectedMember.name}
          memberId={selectedMember.id}
          reviewType={selectedReviewType}
          onSchedule={async (scheduleData) => {
            try {
              // Combine date and time into single Date object
              const [hours, minutes] = scheduleData.scheduledTime.split(':').map(Number)
              const scheduledDateTime = new Date(scheduleData.scheduledDate)
              scheduledDateTime.setHours(hours, minutes, 0, 0)
              
              // Map location to proper format
              const locationMap: Record<string, string> = {
                'virtual': 'Virtual (MS Teams)',
                'manager-office': "People Leader's Office",
                'meeting-room': 'Meeting Room',
                'other': 'Other Location'
              }
              
              // Find the review session ID for this member and review type
              const reviewSessions = await reviewService.getUserReviewCycle(selectedMember.id)
              const session = reviewSessions.find(s => 
                s.review_type.toLowerCase().replace('_', '-') === scheduleData.reviewType
              )
              
              if (!session) {
                toast.error('Check-in not found. Please initialize the check-in cycle first.')
                return
              }
              
              // Schedule the review with notifications
              const result = await reviewService.scheduleReview(
                session.id,
                scheduledDateTime,
                user?.employeeId || '',
                scheduleData.reviewType,
                locationMap[scheduleData.location] || scheduleData.location,
                scheduleData.notes,
                undefined // Let backend determine duration
              )
              
              // Show success message with notification status
              if (result.notifications) {
                const { calendar_created, email_sent, teams_link } = result.notifications
                
                if (calendar_created && email_sent) {
                  toast.success(
                    `Check-in scheduled! Calendar invite and email sent to ${selectedMember.name}.`,
                    { duration: 5000 }
                  )
                  if (teams_link) {
                    if (import.meta.env.DEV) console.log('Teams meeting link:', teams_link)
                  }
                } else if (calendar_created) {
                  toast.success(
                    `Check-in scheduled! Calendar invite sent (email notification failed).`,
                    { duration: 4000 }
                  )
                } else if (email_sent) {
                  toast.success(
                    `Check-in scheduled! Email sent (calendar creation failed).`,
                    { duration: 4000 }
                  )
                } else {
                  toast.success(
                    `Check-in scheduled! (Notifications unavailable - check Azure AD configuration)`,
                    { duration: 4000, icon: '⚠️' }
                  )
                }
              } else {
                toast.success(`Check-in scheduled for ${selectedMember.name}!`)
              }
              
              handleCloseScheduleReview()
              
              // Refresh team data to show updated schedule
              if (user?.employeeId) {
                await loadTeamData()
              }
              
            } catch (error) {
              console.error('Failed to schedule review:', error)
              toast.error(serverReason(error, 'Failed to schedule check-in. Please try again.'))
              // Refused because the check-in has moved on since the page loaded it
              // (completed, held, cancelled): close and bring the team up to date
              if (isConflict(error)) {
                handleCloseScheduleReview()
                await loadTeamData()
              }
            }
          }}
        />
      )}

      {/* Conduct Review Workspace */}
      {selectedMember && conductReviewOpen && (
        <ConductReviewWorkspace
          open={conductReviewOpen}
          onClose={handleCloseConductReview}
          memberName={selectedMember.name}
          memberId={selectedMember.id}
          memberRole={selectedMember.role}
          memberDepartment={selectedMember.fullData?.department ?? undefined}
          managerId={user?.employeeId} // Current manager's ID
          reviewType={currentReviewType}
          scheduledDate={currentReviewScheduledDate}
          userRole="manager"
          reviewSessionId={reviewSessionId} // Load existing session if available
          kpaData={memberKPAData} // Load member's KPA data with objectives and KPIs
          evidence={memberEvidence}
          onAddEvidence={handleAddEvidence}
          onUpdateEvidence={handleUpdateEvidence}
          onDeleteEvidence={handleDeleteEvidence}
          onUploadDocument={handleUploadDocument}
          onUpdateEvidenceClassification={handleClassifyEvidence}
          onRefreshEvidence={async () => {
            if (selectedMember?.id) {
              await loadMemberEvidence(selectedMember.id)
              await loadMemberKPAData(selectedMember.id)
              setDialogRefreshTrigger(prev => prev + 1)
            }
          }}
          onReviewCompleted={() => {
            // Refresh team data including review sessions after review completion
            loadTeamData()
            setDialogRefreshTrigger(prev => prev + 1)
          }}
        />
      )}

    </div>
  )
}
